import { useMutation } from '@src/query';


/**
 * Onboarding / invite-accept mutation hooks (Wave 5). Each wraps the shared
 * `useMutation` engine with the injected-`queryClient`
 * `mutateFn({ apiClient, queryClient })` contract — the corelib query singleton
 * is NOT imported here. Cache operations run inside `mutateFn` (the one place
 * `queryClient` is injected). These flows fire during onboarding / invite
 * acceptance, before any seller-scoped query key exists, so they keep the
 * original blanket `invalidateQueries()` / `clear()` behavior rather than
 * threading a targeted key.
 *
 * Mirrors `query/dashboard/useDashboardMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/**
 * Create the seller (org) at the end of onboarding. Variables: `{ payload }`.
 * After the POST resolves the whole cache is invalidated (blanket — nothing
 * seller-scoped is cached yet) so the app re-reads the new org on the next
 * route load. Returns the created seller.
 */
function useCreateSellerMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, payload }) => apiClient
      .request({ url: '/sellers/', method: 'POST', payload })
      .then((data) => queryClient.invalidateQueries().then(() => data)),
    ...params,
  });
}


/**
 * Accept a staff invite as a brand-new user. Variables: `{ token, payload }`.
 * Raw public request (the page is unauthenticated). After the POST resolves the
 * cache is cleared — a brand-new session, so drop anything cached by a previous
 * account on this tab. Returns the auth payload.
 */
function useAcceptInviteMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, token, payload }) => apiClient
      .request({ url: `/sellers/invites/${token}/accept/`, method: 'POST', useAuth: false, payload })
      .then((data) => {
        queryClient.clear();
        return data;
      }),
    ...params,
  });
}


/**
 * Accept a staff invite as an existing, signed-in member. Variables: `{ token }`.
 * After the authed POST resolves the whole cache is invalidated (blanket) so the
 * newly joined org is picked up. Returns the response data.
 */
function useAcceptInviteAsMemberMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, token }) => apiClient
      .request({ url: `/sellers/invites/${token}/accept/`, method: 'POST' })
      .then((data) => queryClient.invalidateQueries().then(() => data)),
    ...params,
  });
}


export {
  useAcceptInviteAsMemberMutation,
  useAcceptInviteMutation,
  useCreateSellerMutation,
};
